import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { Button, Switch } from '@domcrm/embed-ui';

import {
  acceptCrmAgreement,
  acceptOrgAgreement,
  acceptPersonalDataAgreement,
  acceptPlatformUserRulesAgreement,
  getMeOnboarding,
  onboardingPending,
  type OnboardingResponse
} from '../widget-api/auth-onboarding';
import {
  getMe,
  patchMeProfile,
  postSelectClientOrganization,
  postSetInitialPassword
} from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { setEmbedSession } from '../widget-lib/embed-session-store';
import { generateEmbedPassword } from '../widget-lib/generate-embed-password';
import { PATH } from '../widget-lib/path-routes';
import {
  validateDateOfBirth,
  validateEmail,
  validateFullName,
  validatePasswordPolicy
} from '../widget-lib/validation';
import { LegalAgreementHtmlBlock } from '../widget-ui/legal-agreement-html-block';
import { embedFieldErrorInputClass, EmbedMessageBlock } from '../widget-ui/embed-message-block';

type ProfileForm = {
  full_name: string;
  email: string;
  date_of_birth: string;
  password: string;
  password_confirm: string;
};

const CREATED_BY_STAFF_HINTS: Record<string, string> = {
  admin: 'Эту карточку создал администратор. Проверьте ФИО и заполните email и пароль, чтобы входить самостоятельно.',
  trainer: 'Эту карточку создал тренер. Проверьте ФИО и заполните email и пароль, чтобы входить самостоятельно.'
};

function nextStep(ob: OnboardingResponse): 'pd' | 'rules' | 'crm' | 'profile' | 'org' | 'done' {
  if (ob.need_personal_data_agreement) return 'pd';
  if (ob.need_platform_user_rules) return 'rules';
  if (ob.need_crm_agreement) return 'crm';
  if (ob.need_profile) return 'profile';
  if (ob.need_org_agreement) return 'org';
  return 'done';
}

export default function AgreementsGatePage() {
  useWidgetSeo(
    'Соглашение и онбординг',
    'Принятие правил обслуживания и подтверждение пользовательских соглашений.'
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  const { accessToken, organizationId, bootstrapHint, orgSlug, guestShowcaseOrg, reloadMe, me } = useEmbedBootstrap();
  const [actionError, setActionError] = useState<string | null>(null);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const [pdOk, setPdOk] = useState(false);
  const [rulesOk, setRulesOk] = useState(false);
  const [crmOk, setCrmOk] = useState(false);
  const [orgOk, setOrgOk] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const {
    data: onboarding,
    loading,
    error,
    reload
  } = useAsync(
    async () => {
      if (!accessToken) {
        return null;
      }
      return getMeOnboarding(organizationId, accessToken);
    },
    [accessToken, organizationId]
  );

  const step = onboarding ? nextStep(onboarding) : 'done';

  const {
    data: meProfile,
    loading: meProfileLoading,
    error: meProfileError,
    reload: reloadMeProfile
  } = useAsync(
    async () => {
      if (!accessToken || !onboarding?.need_profile) return null;
      return getMe(accessToken);
    },
    [accessToken, onboarding?.need_profile]
  );

  const passwordFieldsVisible = !meProfileLoading && meProfile?.password_set === false;
  const emailLocked = Boolean(meProfile?.email?.trim() && me?.email_verified_at);
  const creatorHint = meProfile?.created_via ? CREATED_BY_STAFF_HINTS[meProfile.created_via] ?? null : null;
  const profileMissing = [
    meProfile && !String(meProfile.full_name ?? '').trim() ? 'ФИО' : '',
    meProfile && !String(meProfile.email ?? '').trim() ? 'email' : '',
    meProfile && !String(meProfile.date_of_birth ?? '').trim() ? 'дата рождения' : ''
  ].filter(Boolean);
  const hasClientMembership =
    me != null &&
    organizationId != null &&
    (me.memberships ?? []).some(
      (m) => m.organization_id === organizationId && String(m.role).toLowerCase() === 'client'
    );
  const needClientJoin = Boolean(accessToken && me != null && organizationId != null && !hasClientMembership);

  const profileDefaults = useMemo(
    () => ({
      full_name: meProfile?.full_name ?? '',
      email: meProfile?.email ?? '',
      date_of_birth: (meProfile?.date_of_birth ?? '').slice(0, 10),
      password: '',
      password_confirm: ''
    }),
    [meProfile]
  );

  const profileForm = useForm<ProfileForm>({ defaultValues: profileDefaults });

  useEffect(() => {
    profileForm.reset(profileDefaults);
  }, [profileDefaults, profileForm]);

  useEffect(() => {
    setPdOk(false);
    setRulesOk(false);
    setCrmOk(false);
    setOrgOk(false);
  }, [step]);

  if (bootstrapHint && !orgSlug && !accessToken) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="space-y-3 w-full min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Соглашения</h1>
          <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
        </div>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="space-y-3 w-full min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Соглашения</h1>
          <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы принять соглашения.</p>
          <Link
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)]"
            to={`${PATH.signIn}${suffix}`}
          >
            Войти
          </Link>
        </div>
      </div>
    );
  }

  if (loading || !onboarding) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="w-full min-w-0">
          <p className="text-sm text-[var(--text-muted)]">Загрузка условий…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="space-y-3 w-full min-w-0">
          <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
            {error}
          </EmbedMessageBlock>
          <Button type="button" variant="secondary" onClick={() => reload()}>
            Повторить
          </Button>
        </div>
      </div>
    );
  }

  if (needClientJoin) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
          <div className="space-y-4 px-4 pb-6 pt-5 sm:px-5">
            <div className="space-y-1">
              <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Стать клиентом организации</h1>
              <p className="text-sm text-[var(--text-muted)]">
                Вы вошли как сотрудник. Чтобы пользоваться клиентским приложением, подтвердите регистрацию
                как клиент организации. Данные, которые уже заполнены, повторно вводить не нужно.
              </p>
            </div>
            <dl className="space-y-2 rounded-lg bg-[var(--bg-soft)] px-3 py-3 text-sm">
              {me?.phone ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-[var(--text-muted)]">Телефон</dt>
                  <dd className="text-right text-[var(--text)]">{me.phone}</dd>
                </div>
              ) : null}
              {me?.full_name ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-[var(--text-muted)]">ФИО</dt>
                  <dd className="text-right text-[var(--text)]">{me.full_name}</dd>
                </div>
              ) : null}
              {me?.email ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-[var(--text-muted)]">Email</dt>
                  <dd className="text-right text-[var(--text)]">{me.email}</dd>
                </div>
              ) : null}
            </dl>
            {joinError ? (
              <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                {joinError}
              </EmbedMessageBlock>
            ) : null}
            <Button type="button" className="w-full" disabled={joinBusy} onClick={() => void onJoinClient()}>
              {joinBusy ? 'Оформляем…' : 'Стать клиентом организации'}
            </Button>
            <p className="text-xs leading-snug text-[var(--text-muted)]">
              После подтверждения откроется заполнение недостающих полей профиля и соглашений.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!onboardingPending(onboarding)) {
    async function goNext() {
      if (!accessToken) return;
      try {
        const meNow = await getMe(accessToken);
        if (!meNow.email_verified_at && meNow.email_verification_available) {
          navigate(`${PATH.confirmEmail}${suffix}`);
          return;
        }
      } catch { /* fallthrough */ }
      navigate(`${orgSlug ? PATH.schedule : PATH.catalog}${suffix}`);
    }
    const showEmailStep = accessToken != null;
    const emailVerified = Boolean(me?.email_verified_at);
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
          <div className="space-y-5 px-4 pb-6 pt-5 sm:px-5">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Настройка профиля</h1>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </span>
                <span className="text-sm text-[var(--text)]">Соглашения приняты</span>
              </li>
              <li className="flex items-center gap-3">
                {emailVerified ? (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </span>
                ) : (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-[var(--border)] bg-[var(--bg-soft)]">
                    <span className="h-2 w-2 rounded-full bg-[var(--border)]" />
                  </span>
                )}
                <span className={`text-sm ${emailVerified ? 'text-[var(--text)]' : 'text-[var(--text-muted)]'}`}>
                  {emailVerified ? 'Email подтверждён' : 'Подтверждение email'}
                </span>
              </li>
            </ul>
            {showEmailStep ? (
              <div className="border-t border-[var(--border)] pt-4">
                <Button
                  type="button"
                  onClick={() => { void goNext(); }}
                >
                  Продолжить
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const ob = onboarding;

  const inputClassBase =
    'h-8 w-full rounded-sm border-0 bg-[var(--bg-soft)] py-1 text-[13px] text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';
  const inputClass = `${inputClassBase} px-3`;
  const passwordInputClass = `${inputClassBase} pl-3 pr-10`;

  async function onAcceptPd() {
    if (!accessToken) return;
    setActionError(null);
    try {
      await acceptPersonalDataAgreement(ob.personal_data_agreement_id, ob.personal_data_agreement_version, accessToken);
      reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
    }
  }

  async function onAcceptRules() {
    if (!accessToken) return;
    setActionError(null);
    try {
      await acceptPlatformUserRulesAgreement(ob.platform_user_rules_id, ob.platform_user_rules_version, accessToken);
      reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
    }
  }

  async function onAcceptCrm() {
    if (!accessToken) return;
    setActionError(null);
    try {
      await acceptCrmAgreement(ob.crm_agreement_id, ob.crm_agreement_version, accessToken);
      reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
    }
  }

  const onProfileSubmit = profileForm.handleSubmit(async (vals) => {
    if (!accessToken) return;
    setActionError(null);
    setPasswordNotice(null);
    const needsInitialPassword = passwordFieldsVisible;
    try {
      await patchMeProfile(
        {
          full_name: vals.full_name.trim(),
          email: vals.email.trim().toLowerCase(),
          date_of_birth: vals.date_of_birth || null
        },
        accessToken
      );
    } catch (e) {
      setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
      return;
    }
    if (needsInitialPassword) {
      try {
        await postSetInitialPassword(vals.password, accessToken);
      } catch (e) {
        const alreadySet =
          e instanceof ApiError &&
          (e.status === 409 || e.errorType === 'password_already_set' || e.errorCode === 'password_already_set');
        if (!alreadySet) {
          setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
          return;
        }
        setPasswordNotice('Пароль уже установлен — можно входить с email и паролем.');
      }
    }
    reload();
    reloadMeProfile();
  });

  async function onJoinClient() {
    if (!accessToken || organizationId == null) return;
    setJoinBusy(true);
    setJoinError(null);
    try {
      const res = await postSelectClientOrganization(accessToken, { organization_id: organizationId });
      const slug = orgSlug?.trim() || guestShowcaseOrg?.slug?.trim() || `org_${organizationId}`;
      setEmbedSession(slug, {
        accessToken: res.access_token,
        organizationId,
        embedMode: 'client',
        refreshToken: res.refresh_token ?? undefined
      });
      // Новый токен уже содержит client-членство; полная перезагрузка подхватит его
      // и перейдёт к следующему шагу onboarding (профиль/соглашения).
      window.location.reload();
    } catch (e) {
      setJoinError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось стать клиентом организации');
    } finally {
      setJoinBusy(false);
    }
  }

  async function onAcceptOrg() {
    if (!accessToken || organizationId == null) return;
    setActionError(null);
    try {
      await acceptOrgAgreement(ob.org_agreement_id ?? null, organizationId, ob.org_agreement_version, accessToken);
      const slug = orgSlug?.trim() || guestShowcaseOrg?.slug?.trim();
      if (slug) {
        const res = await postSelectClientOrganization(accessToken, { organization_id: organizationId });
        setEmbedSession(slug, {
          accessToken: res.access_token,
          organizationId,
          embedMode: 'client',
          refreshToken: res.refresh_token ?? undefined
        });
        reloadMe();
      }
      reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка');
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
      <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
        <div className="space-y-5 px-4 pb-6 pt-5 sm:px-5">
          <div className="space-y-1">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Соглашения и профиль</h1>
            <p className="text-sm text-[var(--text-muted)]">
              Чтобы пользоваться сервисом, завершите шаги ниже.
            </p>
          </div>

          {step === 'pd' ? (
            <section className="space-y-3 border-t border-[var(--border)] pt-4">
              <h2 className="text-sm font-semibold text-[var(--text)]">Согласие на обработку персональных данных</h2>
              <LegalAgreementHtmlBlock html={ob.personal_data_agreement_text} />
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5">
                <span id="agree-pd-label" className="text-sm leading-snug text-[var(--text)]">
                  Ознакомился(ась) с текстом и принимаю условия
                </span>
                <Switch
                  checked={pdOk}
                  onCheckedChange={setPdOk}
                  aria-labelledby="agree-pd-label"
                  aria-label="Согласие на обработку персональных данных"
                />
              </div>
              <Button type="button" disabled={!pdOk} onClick={() => void onAcceptPd()}>
                Принять
              </Button>
            </section>
          ) : null}

          {step === 'rules' ? (
            <section className="space-y-3 border-t border-[var(--border)] pt-4">
              <h2 className="text-sm font-semibold text-[var(--text)]">Пользовательское соглашение платформы</h2>
              <LegalAgreementHtmlBlock html={ob.platform_user_rules_text} />
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5">
                <span id="agree-rules-label" className="text-sm leading-snug text-[var(--text)]">
                  Ознакомился(ась) с текстом и принимаю условия
                </span>
                <Switch
                  checked={rulesOk}
                  onCheckedChange={setRulesOk}
                  aria-labelledby="agree-rules-label"
                  aria-label="Согласие с пользовательским соглашением платформы"
                />
              </div>
              <Button type="button" disabled={!rulesOk} onClick={() => void onAcceptRules()}>
                Принять
              </Button>
            </section>
          ) : null}

          {step === 'crm' ? (
            <section className="space-y-3 border-t border-[var(--border)] pt-4">
              <h2 className="text-sm font-semibold text-[var(--text)]">Пользовательское соглашение CRM</h2>
              <LegalAgreementHtmlBlock html={ob.crm_agreement_text} />
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5">
                <span id="agree-crm-label" className="text-sm leading-snug text-[var(--text)]">
                  Ознакомился(ась) с текстом и принимаю условия
                </span>
                <Switch
                  checked={crmOk}
                  onCheckedChange={setCrmOk}
                  aria-labelledby="agree-crm-label"
                  aria-label="Согласие с пользовательским соглашением CRM"
                />
              </div>
              <Button type="button" disabled={!crmOk} onClick={() => void onAcceptCrm()}>
                Принять
              </Button>
            </section>
          ) : null}

          {step === 'profile' ? (
            <section className="space-y-3 border-t border-[var(--border)] pt-4">
              <h2 className="text-sm font-semibold text-[var(--text)]">Профиль</h2>
              <p className="text-sm text-[var(--text-muted)]">Заполните обязательные поля, чтобы завершить вход.</p>
              {profileMissing.length > 0 ? (
                <EmbedMessageBlock variant="info" size="field" className="w-full" role="status">
                  Осталось заполнить: {profileMissing.join(', ')}.
                </EmbedMessageBlock>
              ) : null}
              {creatorHint ? (
                <EmbedMessageBlock variant="info" size="field" className="w-full">
                  {creatorHint}
                </EmbedMessageBlock>
              ) : null}
              {meProfileLoading ? (
                <p className="text-sm text-[var(--text-muted)]">Загрузка данных…</p>
              ) : meProfileError ? (
                <div className="space-y-2">
                  <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                    {meProfileError}
                  </EmbedMessageBlock>
                  <Button type="button" variant="secondary" onClick={() => reloadMeProfile()}>
                    Повторить
                  </Button>
                </div>
              ) : meProfile ? (
              <form className="space-y-3" onSubmit={onProfileSubmit} noValidate>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="ag-fn">
                    ФИО *
                  </label>
                  <input
                    id="ag-fn"
                    className={`${inputClass} ${profileForm.formState.errors.full_name ? embedFieldErrorInputClass : ''}`}
                    {...profileForm.register('full_name', {
                      validate: (v) => {
                        const r = validateFullName(v, { required: true });
                        return r === true || r;
                      }
                    })}
                  />
                  {profileForm.formState.errors.full_name?.message ? (
                    <div className="mt-1.5">
                      <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                        {String(profileForm.formState.errors.full_name.message)}
                      </EmbedMessageBlock>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="ag-em">
                    Email *
                  </label>
                  <input
                    id="ag-em"
                    type="email"
                    readOnly={emailLocked}
                    className={`${inputClass} ${emailLocked ? 'cursor-not-allowed opacity-80' : ''} ${profileForm.formState.errors.email ? embedFieldErrorInputClass : ''}`}
                    {...profileForm.register('email', {
                      required: 'Укажите email',
                      validate: (v) => {
                        const r = validateEmail(v, { required: true });
                        return r === true || r;
                      }
                    })}
                  />
                  {emailLocked ? (
                    <p className="mt-1 text-xs leading-snug text-[var(--text-muted)]">
                      Email уже подтверждён — повторно вводить и подтверждать не нужно.
                    </p>
                  ) : null}
                  {profileForm.formState.errors.email?.message ? (
                    <div className="mt-1.5">
                      <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                        {String(profileForm.formState.errors.email.message)}
                      </EmbedMessageBlock>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="ag-dob">
                    Дата рождения *
                  </label>
                  <p className="mb-1 text-xs leading-snug text-[var(--text-muted)]">
                    Обязательно для завершения входа.
                  </p>
                  <input
                    id="ag-dob"
                    type="date"
                    className={`${inputClass} ${profileForm.formState.errors.date_of_birth ? embedFieldErrorInputClass : ''}`}
                    {...profileForm.register('date_of_birth', {
                      validate: (v) => {
                        const r = validateDateOfBirth(v, { required: true });
                        return r === true || r;
                      }
                    })}
                  />
                  {profileForm.formState.errors.date_of_birth?.message ? (
                    <div className="mt-1.5">
                      <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                        {String(profileForm.formState.errors.date_of_birth.message)}
                      </EmbedMessageBlock>
                    </div>
                  ) : null}
                </div>
                {passwordFieldsVisible ? (
                  <>
                    <div>
                      <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="ag-pw">
                        Пароль
                      </label>
                      <div className="relative">
                        <input
                          id="ag-pw"
                          type={showGeneratedPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          className={`${passwordInputClass} ${profileForm.formState.errors.password ? embedFieldErrorInputClass : ''}`}
                          {...profileForm.register('password', {
                            validate: (v) => {
                              const r = validatePasswordPolicy(v, { required: true });
                              return r === true || r;
                            }
                          })}
                        />
                        <button
                          type="button"
                          className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--accent)] hover:bg-[var(--bg-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                          onClick={() => {
                            const next = generateEmbedPassword();
                            profileForm.setValue('password', next, { shouldDirty: true, shouldValidate: true });
                            profileForm.setValue('password_confirm', next, { shouldDirty: true, shouldValidate: true });
                            setShowGeneratedPassword(true);
                          }}
                          aria-label="Сгенерировать пароль"
                          title="Сгенерировать пароль"
                        >
                          <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                      <p className="mt-1 text-xs leading-snug text-[var(--text-muted)]">
                        Не менее 12 символов, хотя бы одна строчная и одна прописная буква, хотя бы одна цифра.
                      </p>
                      {profileForm.formState.errors.password?.message ? (
                        <div className="mt-1.5">
                          <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                            {String(profileForm.formState.errors.password.message)}
                          </EmbedMessageBlock>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="ag-pw2">
                        Пароль ещё раз
                      </label>
                      <input
                        id="ag-pw2"
                        type={showGeneratedPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={`${inputClass} ${profileForm.formState.errors.password_confirm ? embedFieldErrorInputClass : ''}`}
                        {...profileForm.register('password_confirm', {
                          required: 'Повторите пароль.',
                          validate: (v, values) => (v === values.password ? true : 'Пароли не совпадают.')
                        })}
                      />
                      {profileForm.formState.errors.password_confirm?.message ? (
                        <div className="mt-1.5">
                          <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                            {String(profileForm.formState.errors.password_confirm.message)}
                          </EmbedMessageBlock>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}
                <Button type="submit" disabled={profileForm.formState.isSubmitting}>
                  {profileForm.formState.isSubmitting ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </form>
              ) : null}
            </section>
          ) : null}

          {step === 'org' ? (
            <section className="space-y-3 border-t border-[var(--border)] pt-4">
              <h2 className="text-sm font-semibold text-[var(--text)]">Соглашение с организацией</h2>
              {organizationId == null ? (
                <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert">
                  Не удалось определить организацию. Укажите org_slug и при необходимости войдите снова.
                </EmbedMessageBlock>
              ) : (
                <>
                  <LegalAgreementHtmlBlock html={ob.org_agreement_text} />
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5">
                    <span id="agree-org-label" className="text-sm leading-snug text-[var(--text)]">
                      Ознакомился(ась) с текстом и принимаю условия
                    </span>
                    <Switch
                      checked={orgOk}
                      onCheckedChange={setOrgOk}
                      aria-labelledby="agree-org-label"
                      aria-label="Согласие с организацией"
                    />
                  </div>
                  <Button type="button" disabled={!orgOk} onClick={() => void onAcceptOrg()}>
                    Принять
                  </Button>
                </>
              )}
            </section>
          ) : null}

          {passwordNotice ? (
            <EmbedMessageBlock variant="info" size="field" className="w-full">
              {passwordNotice}
            </EmbedMessageBlock>
          ) : null}

          {actionError ? (
            <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
              {actionError}
            </EmbedMessageBlock>
          ) : null}
        </div>
      </div>
    </div>
  );
}
